import React, { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useTheme } from '@material-ui/core/styles';

import DetailsSection from '../DetailsSection';
import { descriptionAndReviewState } from '../helper';
import { getIconColour } from '../../../../helpers/app';
import WallpaperIcon from '../../../../assets/icons/Wallpaper';
import { ExpandContent, SectionRow } from '../styles';
import ImageUpload from '../../../../styles/templates/ImageUpload';
import TooltipInput from '../../../../components/Input/TooltipInput';
import { TOOLTIPS } from '../../../../constants/tooltips';
import { getErrorMessage } from '../../../../helpers/formErrorMessages';
import { ApplicationInterface } from '../../../../interfaces/application';

type PropsType = {
  formHook: UseFormReturn<ApplicationInterface>;
  expand?: boolean;
  onClick: () => void;
  onUpload: (file: File) => void;
  logoUrl?: string;
};

type ExpandPropsType = {
  formHook: UseFormReturn<ApplicationInterface>;
  onUpload: (file: File) => void;
  logoUrl?: string;
};

const ExpandedDescriptionAndPreview: React.FC<ExpandPropsType> = ({
  formHook,
  onUpload,
  logoUrl,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onUpload(acceptedFiles[0]);
    },
    [onUpload],
  );
  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });
  const {
    register,
    formState: { errors },
  } = formHook;

  return (
    <ExpandContent>
      <SectionRow>
        <TooltipInput
          tooltipText={TOOLTIPS.shortDescription}
          inputProps={register('info.shortDescription')}
          id="shortDescription"
          label="Short Preview Description"
          autoComplete="off"
          multiline
          rows={4}
          variant="outlined"
          className="wide"
          focused={false}
          card
        />
      </SectionRow>

      <SectionRow>
        <TooltipInput
          tooltipText={TOOLTIPS.description}
          inputProps={register('info.description')}
          id="description"
          label="Long Description"
          multiline
          rows={4}
          variant="outlined"
          className="wide"
          autoComplete="off"
          focused={false}
          card
        />
      </SectionRow>

      <SectionRow>
        <TooltipInput
          tooltipText={TOOLTIPS.appPurpose}
          inputProps={register('info.dataUsePurpose', { required: true })}
          id="dataUsePurpose"
          label="Application Purpose"
          variant="outlined"
          className="wide"
          autoComplete="off"
          multiline
          rows={4}
          focused={false}
          card
          error={!!errors.info?.dataUsePurpose}
          helperText={getErrorMessage(errors.info?.dataUsePurpose)}
        />
      </SectionRow>

      <SectionRow>
        <div className="ds-profile-icon-wrapper">
          <label className="ds-profile-icon-label" htmlFor="ds-profile-icon">
            Application Icon
          </label>
          <ImageUpload {...getRootProps({ className: `${logoUrl ? 'image' : 'dashed'}` })}>
            {logoUrl ? (
              <>
                <img src={logoUrl} alt="Application Logo" />
                <div>
                  <input {...getInputProps()} id="ds-profile-icon" />
                </div>
              </>
            ) : (
              <>
                <i className="material-icons">photo</i>
                <div className="ds-profile-icon-title">
                  Drag & Drop or browse to upload an image
                </div>

                <div>
                  <input {...getInputProps()} id="ds-profile-icon" />
                </div>
              </>
            )}
          </ImageUpload>
        </div>
      </SectionRow>
    </ExpandContent>
  );
};

const DescriptionAndPreview: React.FC<PropsType> = ({
  formHook,
  expand,
  onClick,
  onUpload,
  logoUrl,
}) => {
  const type = descriptionAndReviewState(formHook);
  const theme = useTheme();

  return (
    <DetailsSection
      icon={<WallpaperIcon stroke={getIconColour(type, theme)} />}
      title="Description and Previews"
      description="Descriptions & preview image for your application"
      expand={expand}
      onClick={onClick}
      type={type}
    >
      <ExpandedDescriptionAndPreview formHook={formHook} onUpload={onUpload} logoUrl={logoUrl} />
    </DetailsSection>
  );
};

export default DescriptionAndPreview;
